import { Suspense, lazy, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import FilterDefault from 'app/components/Filter/FilterDefault';
import TableComponent from 'app/components/Table/TableComponent';
import { DEFAULT_PAGINATION_QUERY, QUERIES } from 'app/components/Table/core/constants';
import { LoadingCard } from 'app/components/Table/loading/LoadingCard';

import LocationFilter from 'app/modules/inventory/items/components/LocationFilter';
import { locationColumns } from 'app/modules/inventory/items/core/columns';
import { getBinsByItemId } from 'app/modules/inventory/items/core/requests';
import { LocationTableWrapper, tableContainerStyle } from 'app/modules/inventory/items/core/styles';
import { LocationFilterParams } from 'app/modules/inventory/items/core/type';

import isNil from 'lodash/isNil';

const AssignItemModal = lazy(() => import('./AssignItemModal'));

const AssignedItemCard = () => {
  const itemId = useParams<{ id: string }>().id;

  const [timestamp, setTimestamp] = useState(0);
  const [isShowFilter, setIsShowFilter] = useState(false);
  const [currentFilter, setCurrentFilter] = useState<LocationFilterParams>({
    locationIds: [],
    rackIds: [],
  });

  useEffect(() => {
    setTimestamp(Date.now());
  }, [currentFilter]);

  const handleGetBinsByItemId = (query: string) => {
    return getBinsByItemId(query, itemId);
  };

  const moreQuery = useMemo(() => {
    let filterQuery = '';

    if (currentFilter.locationIds?.length) {
      currentFilter.locationIds.forEach((id, index) => {
        filterQuery += `&locationIds[${index}]=${id}`;
      });
    }

    if (currentFilter.rackIds?.length) {
      currentFilter.rackIds.forEach((id, index) => {
        filterQuery += `&rackIds[${index}]=${id}`;
      });
    }

    return filterQuery;
  }, [currentFilter]);

  const hasActiveFilters = Object.keys(currentFilter).some(
    (key) =>
      ['locationIds', 'rackIds'].includes(key) &&
      !isNil(currentFilter[key]) &&
      currentFilter[key]?.length > 0,
  );

  const handleClickFilter = (type: 'on' | 'off') => setIsShowFilter(type === 'on');

  const handleApplyFilter = (filter: LocationFilterParams) => {
    const cleanFilter = {
      locationIds: filter.locationIds || [],
      rackIds: filter.rackIds || [],
    };
    setCurrentFilter(cleanFilter);
  };

  const handleClickBodyTable = () => () => {};

  const renderModalForm = () => (
    <Suspense fallback={<LoadingCard />}>
      <AssignItemModal />
    </Suspense>
  );

  const columns = useMemo(() => locationColumns, []);

  return (
    <LocationTableWrapper>
      <TableComponent
        isBorder={false}
        titleModal="Assign To"
        originColumns={columns}
        ModalForm={renderModalForm}
        nameQuery={QUERIES.BINS_BY_ITEM_ID_LIST}
        getList={handleGetBinsByItemId}
        moreQuery={moreQuery}
        defaultState={{
          ...DEFAULT_PAGINATION_QUERY,
          orderBy: 'warehouse.location.name',
          orderDirection: 'ASC',
        }}
        hideSearch
        spacing="md"
        nameBtnAdd="Assign items"
        removeAddText
        tableContainerStyle={tableContainerStyle}
        listHeaderPosition="left"
        onClickBodyTable={handleClickBodyTable}
        customListHeader={
          <FilterDefault
            visible={isShowFilter}
            placement="left-start"
            onClickFilter={handleClickFilter}
            isFilter={hasActiveFilters}
            content={
              isShowFilter && (
                <LocationFilter
                  openFilter={isShowFilter}
                  setIsOpenFilter={setIsShowFilter}
                  onApplyFilter={handleApplyFilter}
                  currentFilter={currentFilter}
                />
              )
            }
          />
        }
        refetchTimestamp={timestamp}
      />
    </LocationTableWrapper>
  );
};

export default AssignedItemCard;
